import { isAffiliateAttributionEnabled } from "./affiliateRollout";

const AFFILIATE_ATTRIBUTION_STORAGE_KEY = "ecotour:portal:affiliate_attribution";
// Keep the browser token only as long as the maximum campaign lookback the
// server supports. The server remains authoritative for each campaign's
// shorter fixed window and never renews it on page refresh or quote replay.
const AFFILIATE_ATTRIBUTION_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

const upper = (value) => String(value || "").trim().toUpperCase();
const safe = (value) => String(value || "").trim();

const normalizeAttribution = (input = {}) => ({
  affiliateCode: upper(input?.affiliateCode || input?.affiliate || input?.aff || ""),
  campaignCode: upper(input?.campaignCode || input?.aff_campaign || ""),
  linkCode: upper(
    input?.linkCode ||
      input?.affiliateLinkCode ||
      input?.r ||
      input?.aff_link ||
      "",
  ),
  couponCode: upper(input?.couponCode || input?.coupon || input?.o || ""),
  landingPath: safe(input?.landingPath || ""),
  capturedAt: safe(input?.capturedAt || ""),
});

const buildAttributionState = (overrides = {}, { includeStored = true } = {}) => {
  const stored = includeStored ? getStoredAffiliateAttribution() : normalizeAttribution();
  const normalizedOverrides = normalizeAttribution(overrides);
  return {
    ...stored,
    ...Object.fromEntries(
      Object.entries(normalizedOverrides).filter(([, value]) => Boolean(value)),
    ),
  };
};

const readStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(AFFILIATE_ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const writeStorage = (value) => {
  if (typeof window === "undefined") return null;
  try {
    window.localStorage.setItem(AFFILIATE_ATTRIBUTION_STORAGE_KEY, JSON.stringify(value));
    return value;
  } catch {
    return value;
  }
};

const clearStorage = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(AFFILIATE_ATTRIBUTION_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in private browsing; expiry still applies in memory.
  }
};

const isCapturedAtActive = (capturedAt = "", now = Date.now()) => {
  const capturedAtMs = new Date(capturedAt).getTime();
  if (!Number.isFinite(capturedAtMs)) return false;
  if (capturedAtMs > now + 5 * 60 * 1000) return false;
  return now - capturedAtMs <= AFFILIATE_ATTRIBUTION_MAX_AGE_MS;
};

const sameAttributionIdentifiers = (left = {}, right = {}) =>
  ["affiliateCode", "campaignCode", "linkCode", "couponCode"].every(
    (key) => safe(left?.[key]) === safe(right?.[key]),
  );

const readParam = (source, keys = []) => {
  if (!source || !keys.length) return "";
  if (source instanceof URLSearchParams) {
    for (const key of keys) {
      const value = source.get(key);
      if (value != null && String(value).trim()) return String(value);
    }
    return "";
  }
  for (const key of keys) {
    const value = source?.[key];
    if (value != null && String(value).trim()) return String(value);
  }
  return "";
};

export function normalizeAffiliateCode(value) {
  return upper(value);
}

export function normalizeCouponCode(value) {
  return upper(value);
}

export function getStoredAffiliateAttribution() {
  if (!isAffiliateAttributionEnabled()) return normalizeAttribution();
  const stored = normalizeAttribution(readStorage() || {});
  if (!hasAffiliateAttribution(stored)) return stored;
  if (!isCapturedAtActive(stored.capturedAt)) {
    clearStorage();
    return normalizeAttribution();
  }
  return stored;
}

export function hasAffiliateAttribution(value = {}) {
  if (!isAffiliateAttributionEnabled()) return false;
  const normalized = normalizeAttribution(value);
  return Boolean(
    normalized.affiliateCode ||
      normalized.campaignCode ||
      normalized.linkCode ||
      normalized.couponCode,
  );
}

export function captureAffiliateAttributionFromSearch(searchOrParams, { pathname = "" } = {}) {
  if (!isAffiliateAttributionEnabled()) {
    return normalizeAttribution();
  }
  const params =
    searchOrParams instanceof URLSearchParams
      ? searchOrParams
      : new URLSearchParams(String(searchOrParams || ""));
  const stored = getStoredAffiliateAttribution();
  const captured = normalizeAttribution({
    affiliateCode: readParam(params, ["affiliateCode", "aff", "affiliate"]),
    campaignCode: readParam(params, ["campaignCode", "aff_campaign"]),
    linkCode: readParam(params, ["r", "linkCode", "affiliateLinkCode", "aff_link"]),
    couponCode: readParam(params, ["o", "couponCode", "coupon"]),
    landingPath: pathname || readParam(params, ["landingPath"]),
    capturedAt: new Date().toISOString(),
  });
  if (!hasAffiliateAttribution(captured)) {
    return stored;
  }
  const capturedAt = sameAttributionIdentifiers(stored, captured) && stored.capturedAt
    ? stored.capturedAt
    : captured.capturedAt;
  // A new referral URL replaces the previous offer as a unit. A bare offer
  // parameter must not borrow a link from another landing.
  const next = { ...captured, capturedAt };
  writeStorage(next);
  return next;
}

export function getEffectiveAffiliateAttribution(overrides = {}, options = {}) {
  return buildAttributionState(overrides, options);
}

export function extractAffiliateAttributionFromItem(item = {}) {
  const metadata = item?.metadata || {};
  const options = item?.options || {};
  const referralContext =
    item?.referralContext ||
    metadata?.referralContext ||
    options?.referralContext ||
    {};
  const acquisition = item?.acquisition || metadata?.acquisition || {};
  return buildAttributionState(
    {
      affiliateCode:
        item?.affiliateCode ||
        metadata?.affiliateCode ||
        referralContext?.affiliateCode ||
        acquisition?.linkedAffiliate?.code ||
        "",
      campaignCode:
        item?.campaignCode ||
        metadata?.campaignCode ||
        referralContext?.campaignCode ||
        acquisition?.campaign?.code ||
        "",
      linkCode:
        item?.linkCode ||
        item?.affiliateLinkCode ||
        metadata?.linkCode ||
        metadata?.affiliateLinkCode ||
        referralContext?.linkCode ||
        acquisition?.link?.code ||
        "",
      couponCode:
        item?.couponCode ||
        metadata?.couponCode ||
        options?.couponCode ||
        referralContext?.couponCode ||
        metadata?.coupon?.publicCode ||
        metadata?.coupon?.code ||
        acquisition?.coupon?.publicCode ||
        acquisition?.coupon?.code ||
        "",
      landingPath:
        metadata?.landingPath ||
        referralContext?.landingPath ||
        "",
      capturedAt:
        metadata?.attributionCapturedAt ||
        referralContext?.capturedAt ||
        "",
    },
    { includeStored: false },
  );
}

export function buildAffiliateAttributionKeyPart(context = {}) {
  if (!isAffiliateAttributionEnabled()) return "";
  const normalized = buildAttributionState(context);
  const parts = [];
  if (normalized.affiliateCode) parts.push(`aff:${normalized.affiliateCode}`);
  if (normalized.campaignCode) parts.push(`campaign:${normalized.campaignCode}`);
  if (normalized.linkCode) parts.push(`link:${normalized.linkCode}`);
  return parts.join("#");
}

export function mergeAffiliateAttributionIntoPayload(payload = {}, overrides = {}, options = {}) {
  const {
    acquisition: _acquisition,
    incentive: _incentive,
    affiliateCommission: _affiliateCommission,
    ...next
  } = payload || {};
  if (!isAffiliateAttributionEnabled()) return next;
  const attribution = buildAttributionState(overrides, options);
  if (attribution.affiliateCode) next.affiliateCode = attribution.affiliateCode;
  if (attribution.campaignCode) next.campaignCode = attribution.campaignCode;
  if (attribution.linkCode) next.linkCode = attribution.linkCode;
  if (attribution.couponCode && !safe(next.couponCode)) next.couponCode = attribution.couponCode;
  if (attribution.landingPath) next.landingPath = attribution.landingPath;
  if (attribution.capturedAt) next.attributionCapturedAt = attribution.capturedAt;
  return next;
}

export function mergeAffiliateAttributionIntoMetadata(metadata = {}, overrides = {}, options = {}) {
  const {
    acquisition: _acquisition,
    incentive: _incentive,
    affiliateCommission: _affiliateCommission,
    ...publicMetadata
  } = metadata || {};
  if (!isAffiliateAttributionEnabled()) return publicMetadata;
  const attribution = buildAttributionState(overrides, options);
  return {
    ...publicMetadata,
    ...(attribution.couponCode ? { couponCode: attribution.couponCode } : {}),
    ...(attribution.affiliateCode ? { affiliateCode: attribution.affiliateCode } : {}),
    ...(attribution.campaignCode ? { campaignCode: attribution.campaignCode } : {}),
    ...(attribution.linkCode ? { linkCode: attribution.linkCode } : {}),
    ...(attribution.landingPath ? { landingPath: attribution.landingPath } : {}),
    ...(attribution.capturedAt ? { attributionCapturedAt: attribution.capturedAt } : {}),
    referralContext: attribution,
  };
}

export function getPublicAffiliateOfferNotice({ incentive = null, coupon = null } = {}) {
  const publicOffer = incentive?.publicOffer || incentive?.policy?.publicOffer || {
    visibility: coupon?.publicAttributionMode,
    label: coupon?.publicOfferLabel,
    hasCustomerBenefit: Number(coupon?.appliedDiscount || coupon?.discountAmount || 0) > 0,
  };
  const visibility = safe(publicOffer?.visibility).toLowerCase() || "hidden";
  const hasCustomerBenefit = Object.prototype.hasOwnProperty.call(publicOffer || {}, "hasCustomerBenefit")
    ? Boolean(publicOffer.hasCustomerBenefit)
    : Number(coupon?.appliedDiscount || coupon?.discountAmount || 0) > 0;
  if (visibility === "hidden" || !hasCustomerBenefit) return null;
  const label = safe(publicOffer?.label);
  return {
    title: visibility === "generic" ? "Partner offer" : label || "Partner offer",
    description: "An approved outreach promotion was applied to your booking.",
  };
}

export { AFFILIATE_ATTRIBUTION_STORAGE_KEY, AFFILIATE_ATTRIBUTION_MAX_AGE_MS };
